/**
 * Metrics service for tracking performance and operations
 */

import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import * as Ref from 'effect/Ref';

export interface MetricValue {
  count: number;
  sum: number;
  min: number;
  max: number;
  avg: number;
}

export interface MetricsService {
  readonly recordDuration: (name: string, durationMs: number) => Effect.Effect<void, never>;
  readonly recordCount: (name: string, count: number) => Effect.Effect<void, never>;
  readonly increment: (name: string) => Effect.Effect<void, never>;
  readonly getMetric: (name: string) => Effect.Effect<MetricValue | null, never>;
  readonly getAllMetrics: () => Effect.Effect<ReadonlyMap<string, MetricValue>, never>;
  readonly reset: () => Effect.Effect<void, never>;
}

export class Metrics extends Context.Tag('Metrics')<Metrics, MetricsService>() {}

function updateMetricValue(existing: MetricValue | null, value: number): MetricValue {
  if (!existing) {
    return { count: 1, sum: value, min: value, max: value, avg: value };
  }

  const newCount = existing.count + 1;
  const newSum = existing.sum + value;
  return {
    count: newCount,
    sum: newSum,
    min: Math.min(existing.min, value),
    max: Math.max(existing.max, value),
    avg: newSum / newCount,
  };
}

export const MetricsLive = Layer.effect(
  Metrics,
  Effect.gen(function* () {
    const metricsRef = yield* Ref.make(new Map<string, MetricValue>());

    return {
      recordDuration: (name, durationMs) =>
        Ref.update(metricsRef, metrics => {
          const updated = new Map(metrics);
          updated.set(name, updateMetricValue(metrics.get(name) || null, durationMs));
          return updated;
        }),

      recordCount: (name, count) =>
        Ref.update(metricsRef, metrics => {
          const updated = new Map(metrics);
          updated.set(name, updateMetricValue(metrics.get(name) || null, count));
          return updated;
        }),

      increment: (name) =>
        Ref.update(metricsRef, metrics => {
          const updated = new Map(metrics);
          const existing = metrics.get(name) || { count: 0, sum: 0, min: 0, max: 0, avg: 0 };
          updated.set(name, { ...existing, count: existing.count + 1 });
          return updated;
        }),

      getMetric: (name) =>
        Ref.get(metricsRef).pipe(
          Effect.map(metrics => metrics.get(name) || null)
        ),

      getAllMetrics: () =>
        Ref.get(metricsRef).pipe(
          Effect.map(metrics => new Map(metrics) as ReadonlyMap<string, MetricValue>)
        ),

      reset: () => Ref.set(metricsRef, new Map()),
    };
  })
);
